import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Monitor, cronToSchedule } from '../../../types/monitoring';

interface MonitorsSectionProps {
  monitors: Monitor[];
  onOpenSettings: (tab: string, itemId?: string) => void;
  onNewMonitor?: () => void;
}

export const MonitorsSection: React.FC<MonitorsSectionProps> = ({
  monitors,
  onOpenSettings,
  onNewMonitor,
}) => {
  const navigate = useNavigate();

  if (monitors.length === 0) return null;

  const handleMonitorClick = (monitor: Monitor) => {
    if (monitor.lead_table_id) {
      navigate(`/datasets/${monitor.lead_table_id}`);
      setTimeout(() => onOpenSettings('monitors', monitor.id), 100);
    } else {
      onOpenSettings('monitors', monitor.id);
    }
  };

  return (
    <section className="mb-10">
      <div className="bg-white border border-neutral-200 rounded-2xl p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[13px] font-medium text-neutral-400 uppercase tracking-wider">
            Monitors
          </h2>
          <button
            onClick={() => onOpenSettings('monitors')}
            className="text-[11px] text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            Manage
          </button>
        </div>

        <div className="hidden sm:flex items-center gap-4 px-3 -mx-3 pb-2 mb-1 border-b border-neutral-100">
          <div className="w-1.5" />
          <span className="flex-1 text-[10px] font-medium text-neutral-400 uppercase tracking-wide">Name</span>
          <span className="text-[10px] font-medium text-neutral-400 uppercase tracking-wide w-14 text-right">Results</span>
          <span className="text-[10px] font-medium text-neutral-400 uppercase tracking-wide w-16 text-right">Schedule</span>
        </div>

        <div className="space-y-0">
          {monitors.slice(0, 4).map((monitor) => (
            <div
              key={monitor.id}
              onClick={() => handleMonitorClick(monitor)}
              className="group flex items-center gap-3 sm:gap-4 py-2.5 px-2 sm:px-3 -mx-2 sm:-mx-3 rounded-lg cursor-pointer hover:bg-neutral-50 transition-colors"
            >
              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                monitor.status === 'enabled'
                  ? 'bg-green-500'
                  : monitor.status === 'error'
                  ? 'bg-red-500'
                  : 'bg-neutral-300'
              }`} />
              <span className="flex-1 text-[13px] text-neutral-900 truncate">
                {monitor.name}
              </span>
              <span className="text-[13px] text-neutral-500 tabular-nums w-10 sm:w-14 text-right">
                {(monitor as any).total_results_found || 0}
              </span>
              <span className="hidden sm:block text-[12px] text-neutral-400 w-16 text-right">
                {monitor.cron_expression
                  ? cronToSchedule(monitor.cron_expression, monitor.timezone).frequency.charAt(0).toUpperCase() + cronToSchedule(monitor.cron_expression, monitor.timezone).frequency.slice(1)
                  : '—'}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-3 pt-3 border-t border-neutral-100">
          <button
            onClick={() => onNewMonitor ? onNewMonitor() : onOpenSettings('monitors')}
            className="flex items-center gap-2 text-[13px] text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>New monitor</span>
          </button>
        </div>
      </div>
    </section>
  );
};
